export function formatCountdown(time: Date) {
  const timeLeft = time.getTime() - Date.now();
  if (timeLeft <= 0) return `00:00:00`;
  const hours = Math.floor(timeLeft / (1000 * 60 * 60));
  const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
  if (timeLeft < 60_000) {
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

export function assert(condition: boolean): asserts condition {
  if (!condition) throw new Error('failed assertion');
}

function inPlaceShuffle<T>(array: T[]): T[] {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j]!, array[i]!];
  }
  return array;
}

export function shuffle<T>(array: T[]): T[] {
  return inPlaceShuffle([...array]);
}

export function sample<T>(array: T[]): T {
  const [item] = shuffle(array);
  assert(item !== undefined);
  return item;
}

export function sampleMany<T>(array: T[], count: number): T[] {
  assert(count <= array.length);
  const shuffled = shuffle(array);
  return shuffled.slice(0, count);
}

export function randomInRange(min: number, max: number) {
  return Math.random() * (max - min) + min;
}

export function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function seededHash(a: number, b: number, c: number): number {
  let h = (2166136261 ^ (a >>> 0)) >>> 0;
  h = Math.imul(h ^ (b + 0x9e3779b9), 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (c + 0xc2b2ae35), 0x27d4eb2f) >>> 0;
  return h >>> 0;
}

export function randomBetweenFromSeed(min: number, max: number, seedA: number, seedB: number): number {
  const h = seededHash(seedA, seedB, 0);
  const t = (h % 1000000) / 1000000;
  return min + t * (max - min);
}

export function randomUnitFromSeed(a: number, b: number, c: number): number {
  const h = seededHash(a, b, c);
  return (h % 1000000) / 1000000;
}

export function crossed(prev: number, next: number, threshold: number) {
  return prev < threshold && next >= threshold;
}

export function fbm1D(x: number, octaves: number, seed: number): number {
  let amp = 0.5;
  let sum = 0;
  let norm = 0;
  let freq = 1;

  for (let o = 0; o < octaves; o++) {
    sum += amp * (valueNoise1D(x * freq, seed + o * 19) * 2 - 1);
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }

  return norm > 0 ? sum / norm : 0;
}

function hash1(n: number): number {
  return fract(Math.sin(n * 127.1) * 43758.5453123);
}

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function fract(n: number): number {
  return n - Math.floor(n);
}

export function valueNoise1D(x: number, seed: number): number {
  const i0 = Math.floor(x);
  const i1 = i0 + 1;
  const t = smoothstep(x - i0);
  const v0 = hash1(i0 + seed * 101.3);
  const v1 = hash1(i1 + seed * 101.3);
  return lerp(v0, v1, t);
}
